import type { AweniContext } from '../context/AweniContext';

type SurfaceId = 'yawmi' | 'hisni';

interface ChipDef {
  id: string;
  /** Emoji or single-char icon glyph rendered before the label (Hisni uses these). */
  icon?: string;
  /** Path to SVG icon (currentColor stroke — adapts to chip text color).
   * Takes precedence over `icon` when both are set. Yawmi uses these per
   * Contentov 2026-05-14 design pack at /aweni/icons/yawmi/. */
  iconSrc?: string;
  hash?: string;
  href?: string;
}

const YAWMI_CHIPS: ChipDef[] = [
  { id: 'calendar', iconSrc: '/aweni/icons/yawmi/icon-calendar.svg', hash: '#/yawmi/calendar' },
  { id: 'habits', iconSrc: '/aweni/icons/yawmi/icon-habits.svg', hash: '#/yawmi/habits' },
  { id: 'alarms', iconSrc: '/aweni/icons/yawmi/icon-alarms.svg', hash: '#/yawmi/alarms' },
  { id: 'reminders', iconSrc: '/aweni/icons/yawmi/icon-reminders.svg', hash: '#/yawmi/reminders' },
  { id: 'duas', iconSrc: '/aweni/icons/yawmi/icon-duas.svg', hash: '#/yawmi/duas' },
  { id: 'health', iconSrc: '/aweni/icons/yawmi/icon-health.svg', hash: '#/yawmi/health' },
  { id: 'people', iconSrc: '/aweni/icons/yawmi/icon-people.svg', hash: '#/yawmi/people' },
  { id: 'history', iconSrc: '/aweni/icons/yawmi/icon-history.svg', hash: '#/yawmi/history' },
];

const HISNI_CHIPS: ChipDef[] = [
  { id: 'quran', icon: '📖', href: '/hisni/#/start' },
  { id: 'adhkar', icon: '📿', href: '/hisni/#/adhkar' },
  { id: 'hadith', icon: '📜', href: '/hisni/#/hadith' },
  { id: 'dua', icon: '🤲', href: '/hisni/#/duas' },
  { id: 'seerah', icon: '🕌', href: '/hisni/#/seerah' },
  { id: 'tools', icon: '🧭', href: '/hisni/#/tools' },
];

let _ctx: AweniContext | null = null;
let _row: HTMLElement | null = null;
let _surface: SurfaceId | null = null;
let _topOffset = 48;

export function mountChipRow(ctx: AweniContext): void {
  _ctx = ctx;
  if (_row) return;
  const row = document.createElement('nav');
  row.id = 'aweni-chip-row';
  row.style.cssText = `
    position: fixed;
    top: ${_topOffset}px;
    left: 0;
    right: 0;
    height: 40px;
    z-index: 99;
    display: none;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    scrollbar-width: none;
    padding: 6px 12px;
    background: var(--topbar-bg);
    border-bottom: 1px solid var(--border);
    direction: ${ctx.i18n.dir || 'ltr'};
  `;
  row.setAttribute('aria-label', 'Surface navigation');
  row.setAttribute('role', 'tablist');
  row.className = 'ln-chip-row';
  document.body.appendChild(row);
  _row = row;
}

export function setChipRowTop(offsetPx: number): void {
  _topOffset = offsetPx;
  if (_row) _row.style.top = `${offsetPx}px`;
}

export function setChipRowSurface(moduleId: string): void {
  const surface = moduleId === 'yawmi'
    ? 'yawmi'
    : (moduleId === 'hisni' || moduleId === 'quran' ? 'hisni' : null);
  _surface = surface;
  _render();
}

function _render(): void {
  if (!_row || !_ctx) return;
  const chips = _surface === 'yawmi' ? YAWMI_CHIPS : _surface === 'hisni' ? HISNI_CHIPS : [];
  _row.innerHTML = '';

  if (!chips.length) {
    _row.style.display = 'none';
    _updateViewportPadding(false);
    return;
  }

  const active = _resolveActiveChip(_surface);
  for (const chip of chips) {
    const isActive = chip.id === active;
    const btn = document.createElement(chip.href ? 'a' : 'button');
    btn.setAttribute('role', 'tab');
    btn.setAttribute('aria-selected', String(isActive));
    if (chip.href) {
      (btn as HTMLAnchorElement).href = chip.href;
    } else {
      (btn as HTMLButtonElement).type = 'button';
    }
    const labelText = _ctx.i18n.t('chip_' + chip.id);
    if (chip.iconSrc) {
      // SVG icon via <img> with currentColor stroke — adapts to chip text color.
      const iconImg = document.createElement('img');
      iconImg.src = chip.iconSrc;
      iconImg.alt = '';
      iconImg.setAttribute('aria-hidden', 'true');
      iconImg.width = 16;
      iconImg.height = 16;
      iconImg.style.cssText = 'margin-inline-end:6px;vertical-align:middle;flex-shrink:0';
      const labelSpan = document.createElement('span');
      labelSpan.textContent = labelText;
      labelSpan.style.cssText = 'vertical-align:middle';
      btn.appendChild(iconImg);
      btn.appendChild(labelSpan);
      btn.setAttribute('aria-label', labelText);
    } else if (chip.icon) {
      const iconSpan = document.createElement('span');
      iconSpan.textContent = chip.icon;
      iconSpan.setAttribute('aria-hidden', 'true');
      iconSpan.style.cssText = 'font-size:14px;line-height:1;margin-inline-end:6px;display:inline-block;vertical-align:middle';
      const labelSpan = document.createElement('span');
      labelSpan.textContent = labelText;
      labelSpan.style.cssText = 'vertical-align:middle';
      btn.appendChild(iconSpan);
      btn.appendChild(labelSpan);
      btn.setAttribute('aria-label', labelText);
    } else {
      btn.textContent = labelText;
    }
    btn.style.cssText = `
      flex: 0 0 auto;
      min-height: 32px;
      padding: 6px 14px;
      border-radius: 999px;
      border: 1px solid ${isActive ? 'var(--accent)' : 'var(--border)'};
      background: ${isActive ? 'var(--accent)' : 'var(--bg-card, var(--bg-secondary))'};
      color: ${isActive ? 'var(--accent-text)' : 'var(--text-primary)'};
      font-size: 13px;
      font-weight: ${isActive ? '700' : '600'};
      text-decoration: none;
      white-space: nowrap;
      cursor: pointer;
      box-shadow: ${isActive ? 'var(--ln-shadow-sm, 0 1px 3px rgba(0,0,0,.08))' : 'none'};
      transition: background var(--motion-duration-fast, 150ms) var(--motion-ease-standard, ease);
    `;

    if (_surface === 'yawmi' && chip.hash) {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        // Per Motionov 3-surface design: chip row is the canonical tab nav for
        // Yawmi. YawmiModule listens for `aweni:yawmi:tab` and swaps panels.
        const nextHash = chip.hash || '';
        if (nextHash && location.hash !== nextHash) location.hash = nextHash;
        window.dispatchEvent(new CustomEvent('aweni:yawmi:tab', { detail: { tab: chip.id } }));
        _render();
      });
    }

    _row.appendChild(btn);
  }

  _row.style.display = 'flex';
  _updateViewportPadding(true);
}

function _resolveActiveChip(surface: SurfaceId | null): string {
  const hash = location.hash || '';
  if (surface === 'yawmi') {
    if (hash.includes('/yawmi/habits')) return 'habits';
    if (hash.includes('/yawmi/alarms')) return 'alarms';
    if (hash.includes('/yawmi/reminders')) return 'reminders';
    if (hash.includes('/yawmi/duas')) return 'duas';
    if (hash.includes('/yawmi/health')) return 'health';
    if (hash.includes('/yawmi/people')) return 'people';
    if (hash.includes('/yawmi/history')) return 'history';
    return 'calendar';
  }
  if (surface === 'hisni') {
    if (hash.includes('/hadith')) return 'hadith';
    if (hash.includes('/dua') || hash.includes('/duas')) return 'dua';
    if (hash.includes('/adhkar')) return 'adhkar';
    if (hash.includes('/seerah')) return 'seerah';
    if (hash.includes('/tools')) return 'tools';
    return 'quran';
  }
  return '';
}

function _updateViewportPadding(hasChips: boolean): void {
  const viewport = document.getElementById('aweni-main');
  if (!viewport) return;
  viewport.style.paddingTop = hasChips
    ? 'calc(88px + env(safe-area-inset-top, 0px))'
    : '';
}
